import { computed, Injectable, signal } from '@angular/core';

export interface User {
    userId: number;
    email: string;
    name: string;
    role: 'SUPERADMIN' | 'ADMIN' | 'AGENT';
    /** Permisos granulares efectivos (ya expandidos por el backend). Vacío para SUPERADMIN (tiene todos). */
    permissions?: string[];
    teamIds: number[];
    zohoLinked: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AuthSignalService {
    private userSignal = signal<User | null>(null);

    user = computed(() => this.userSignal());
    isAuthenticated = computed(() => !!this.userSignal());
    isAdmin = computed(() => ['ADMIN', 'SUPERADMIN'].includes(this.userSignal()?.role ?? ''));
    isSuperAdmin = computed(() => this.userSignal()?.role === 'SUPERADMIN');

    /** Conjunto de permisos del usuario actual (reactivo). */
    private permsSet = computed(() => new Set(this.userSignal()?.permissions ?? []));

    setUser(user: User | null) {
        if (user) {
            user.zohoLinked = user.zohoLinked || localStorage.getItem('zohoLinkedOverride') === 'true';
        }
        this.userSignal.set(user);
    }

    setZohoLinked(linked: boolean) {
        const u = this.userSignal();
        if (u) {
            this.setUser({ ...u, zohoLinked: linked });
            if (linked) localStorage.setItem('zohoLinkedOverride', 'true');
        }
    }

    // ── Permisos ────────────────────────────────────────────────────────────
    /** ¿Tiene ESTE permiso? El SUPERADMIN siempre. */
    has(permission: string): boolean {
        const u = this.userSignal();
        if (!u) return false;
        if (u.role === 'SUPERADMIN') return true;
        return this.permsSet().has(permission);
    }

    /** ¿Tiene AL MENOS UNO de estos permisos? (útil para grupos del menú) */
    hasAny(...permissions: string[]): boolean {
        const u = this.userSignal();
        if (!u) return false;
        if (u.role === 'SUPERADMIN') return true;
        const set = this.permsSet();
        return permissions.some((p) => set.has(p));
    }

    /** ¿Tiene TODOS estos permisos? */
    hasAll(...permissions: string[]): boolean {
        const u = this.userSignal();
        if (!u) return false;
        if (u.role === 'SUPERADMIN') return true;
        const set = this.permsSet();
        return permissions.every((p) => set.has(p));
    }

    logout() {
        this.userSignal.set(null);
        // Token is an HttpOnly cookie — the server clears it via POST /auth/logout
        // Only clean up non-sensitive local state here
        localStorage.removeItem('zohoLinkedOverride');
    }
}
